import { useRef, useState } from "react";
import {
  Send,
  Loader2,
  Paperclip,
  MapPin,
  Users,
  LayoutList,
  FileText,
  SmilePlus,
} from "lucide-react";
import { enviarTexto } from "../../services/mensagensApi";

/**
 * Barra de entrada de mensagem do chat.
 * Quando janelaAberta === false somente o botão de template fica habilitado.
 *
 * @param {{
 *   conversaId: number,
 *   contaId: number,
 *   janelaAberta: boolean,
 *   onSent: (mensagem: object) => void,
 *   onOpenTemplate: () => void,
 *   onOpenMedia: () => void,
 *   onOpenLocation: () => void,
 *   onOpenContact: () => void,
 *   onOpenInteractive: () => void,
 * }} props
 */
export default function ChatInput({
  conversaId,
  contaId,
  janelaAberta,
  onSent,
  onOpenTemplate,
  onOpenMedia,
  onOpenLocation,
  onOpenContact,
  onOpenInteractive,
}) {
  const [texto,    setTexto]    = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro,     setErro]     = useState(null);
  const textareaRef = useRef(null);

  const janelaFechada  = janelaAberta === false;
  const podEnviarTexto = !janelaFechada && texto.trim().length > 0 && !enviando;

  async function handleEnviar() {
    if (!podEnviarTexto) return;
    const textoEnviar = texto.trim();
    setTexto("");
    setErro(null);
    setEnviando(true);
    textareaRef.current?.focus();
    try {
      // POST /mensagens retorna apenas { jobId, enfileirado } (envio assíncrono
      // via BullMQ). A mensagem real só vem ~1s depois pelo socket. Pra UX
      // imediata, criamos uma mensagem OTIMISTA bem formada com tipo válido.
      // ChatView.handleMensagemNew dedupa quando a real chegar (mesmo texto
      // outbound em janela curta).
      const otimista = {
        id: `temp-${Date.now()}`,
        tipo: "text",
        direcao: "out",
        conteudo: { text: textoEnviar },
        data_criacao: new Date().toISOString(),
        status_meta: "pending",
        _otimista: true,
      };
      onSent(otimista);
      await enviarTexto(conversaId, textoEnviar);
    } catch (err) {
      setErro(err?.response?.data?.error ?? "Erro ao enviar mensagem.");
      setTexto(textoEnviar);
    } finally {
      setEnviando(false);
    }
  }

  function handleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleEnviar();
    }
  }

  const acoes = [
    { id: "media",       icon: Paperclip,  label: "Enviar arquivo",        onClick: onOpenMedia       },
    { id: "location",    icon: MapPin,      label: "Enviar localização",    onClick: onOpenLocation    },
    { id: "contact",     icon: Users,       label: "Enviar contato",        onClick: onOpenContact     },
    { id: "interactive", icon: LayoutList,  label: "Mensagem interativa",   onClick: onOpenInteractive },
    { id: "template",    icon: FileText,    label: "Enviar template",       onClick: onOpenTemplate, sempreAtivo: true },
  ];

  return (
    <div className="border-t border-white/[0.06] bg-base flex-shrink-0">
      {/* Aviso janela fechada */}
      {janelaFechada && (
        <div
          className="px-4 py-2 bg-amber-950 border-b border-amber-800 text-xs text-amber-300 flex items-center gap-2"
          role="status"
          aria-live="polite"
        >
          <SmilePlus className="w-3.5 h-3.5 flex-shrink-0" aria-hidden="true" />
          Janela de 24 h expirada. Apenas templates podem ser enviados.
        </div>
      )}

      {/* Erro de envio */}
      {erro && (
        <div
          className="px-4 py-1.5 bg-red-950 border-b border-red-800 text-xs text-red-300"
          role="alert"
        >
          {erro}
        </div>
      )}

      <div className="flex items-end gap-2 px-3 py-2">
        {/* Toolbar */}
        <div className="flex items-center gap-0.5 pb-1" role="toolbar" aria-label="Ações de mensagem">
          {acoes.map(({ id, icon: Icon, label, onClick, sempreAtivo }) => {
            const desabilitado = janelaFechada && !sempreAtivo;
            return (
              <button
                key={id}
                type="button"
                onClick={desabilitado ? undefined : onClick}
                disabled={desabilitado}
                aria-label={label}
                title={label}
                className={`p-1.5 rounded transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${
                  desabilitado
                    ? "text-ink-5 cursor-not-allowed"
                    : id === "template"
                    ? "text-brand-400 hover:bg-brand-900 hover:text-brand-300"
                    : "text-ink-4 hover:bg-surface-2 hover:text-ink-2"
                }`}
              >
                <Icon className="w-4 h-4" aria-hidden="true" />
              </button>
            );
          })}
        </div>

        {/* Textarea */}
        <textarea
          ref={textareaRef}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={janelaFechada || enviando}
          placeholder={
            janelaFechada
              ? "Janela expirada — use um template"
              : "Escreva uma mensagem… (Enter para enviar, Shift+Enter para nova linha)"
          }
          aria-label="Campo de mensagem"
          rows={1}
          className="flex-1 resize-none bg-surface-2 border border-white/[0.08] rounded-xl px-3 py-2 text-xs text-ink-2 placeholder:text-ink-5 focus:outline-none focus:border-brand-600 transition-colors leading-relaxed max-h-32 overflow-y-auto scrollbar-thin scrollbar-track-zinc-800 scrollbar-thumb-zinc-600 disabled:opacity-40 disabled:cursor-not-allowed"
          style={{ minHeight: "2.25rem" }}
          onInput={(e) => {
            e.target.style.height = "auto";
            e.target.style.height = Math.min(e.target.scrollHeight, 128) + "px";
          }}
        />

        {/* Enviar */}
        <button
          type="button"
          onClick={handleEnviar}
          disabled={!podEnviarTexto}
          aria-label="Enviar mensagem"
          className="mb-0.5 flex-shrink-0 p-2 rounded-xl bg-brand-600 hover:bg-brand-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
        >
          {enviando
            ? <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
            : <Send     className="w-4 h-4"              aria-hidden="true" />}
        </button>
      </div>
    </div>
  );
}
